import {
  Activity,
  Brain,
  Briefcase,
  Building,
  ClipboardCheck,
  Gavel,
  Globe,
  HeartPulse,
  Landmark,
  Plane,
  ScrollText,
  Stethoscope,
  Users,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

// Every service area, its homepage card, and the page section it links to.
// SERVICES MARKED IN THE REPORT AS UNCONFIRMED NEED DR SYED'S SIGN-OFF.

export type ServiceBlock = {
  id: string
  title: string
  icon: LucideIcon
  cardSummary: string
  intro: string
  instructions: string[]
  questions: string[]
  note?: string
}

export type ServicePageContent = {
  path: string
  heroTitle: string
  heroSubtitle: string
  intro: string
  assessmentNote: string
  blocks: ServiceBlock[]
  related: Array<{ label: string; path: string }>
}

const criminal: ServiceBlock = {
  id: 'criminal',
  title: 'Criminal Psychiatric Reports and Fitness to Plead',
  icon: Gavel,
  cardSummary:
    'Fitness to plead and stand trial, mental state at the time of an alleged offence, and psychiatric evidence for sentencing.',
  intro:
    'Dr Syed considers instructions from defence solicitors, the Crown Prosecution Service and the court where a defendant’s mental health may be relevant to the proceedings.',
  instructions: [
    'Fitness to plead and stand trial',
    'Mental state at the time of the alleged offence',
    'Psychiatric reports for sentencing and mitigation',
    'Vulnerability and the need for adjustments or special measures at trial',
  ],
  questions: [
    'Does the defendant have a mental disorder, and what is the diagnosis?',
    'Can the defendant understand the charge, follow the proceedings, instruct their lawyers, challenge a juror and give evidence?',
    'What was the defendant’s mental state at the material time, and how might it relate to the alleged offence?',
    'Is a hospital order or other psychiatric disposal under the Mental Health Act 1983 clinically appropriate?',
    'What adjustments would help the defendant participate effectively?',
  ],
}

const parole: ServiceBlock = {
  id: 'parole',
  title: 'Parole Board and Licence-Condition Assessments',
  icon: ClipboardCheck,
  cardSummary:
    'Psychiatric opinion on mental state, risk and treatment needs to inform Parole Board decisions and licence conditions.',
  intro:
    'Dr Syed considers instructions from prisoners’ solicitors and other parties preparing evidence for Parole Board reviews and licence-condition decisions.',
  instructions: [
    'Psychiatric reports for Parole Board oral hearings and paper reviews',
    'Assessments relevant to proposed licence conditions',
    'Reviews of mental health and treatment needs before release or progression',
  ],
  questions: [
    'Does the prisoner have a current mental disorder, and how is it being managed?',
    'How, if at all, does the mental disorder relate to the risk the prisoner presents?',
    'What treatment, support or monitoring would be needed in the community?',
    'Are the proposed licence conditions clinically appropriate and workable?',
  ],
}

const extradition: ServiceBlock = {
  id: 'extradition',
  title: 'Extradition',
  icon: Plane,
  cardSummary:
    'Psychiatric evidence on mental condition and risk where extradition may be unjust or oppressive.',
  intro:
    'Dr Syed considers instructions from solicitors acting for requested persons where mental health may be relevant to whether extradition should proceed.',
  instructions: [
    'Reports addressing mental condition under section 25 or section 91 of the Extradition Act 2003',
    'Suicide and self-harm risk assessments',
    'Fitness to participate in extradition proceedings',
  ],
  questions: [
    'Does the requested person have a mental disorder, and how severe is it?',
    'What is the risk of suicide or self-harm, including at the point of extradition?',
    'What effect would extradition be likely to have on their mental health?',
    'What treatment do they currently need?',
  ],
}

const prison: ServiceBlock = {
  id: 'prison',
  title: 'Prison Assessments',
  icon: Building,
  cardSummary:
    'Assessments in prisons and secure settings for criminal, parole, immigration and civil proceedings.',
  intro:
    'Where an assessment cannot take place by video, Dr Syed considers visiting prisons and other secure settings to assess the person in person.',
  instructions: [
    'Face-to-face assessments in prisons and secure settings',
    'Assessments for any of the proceedings on this site where the person is in custody',
  ],
  questions: [],
  note: 'Practical arrangements are agreed in advance with the instructing solicitor and the establishment, including legal visit booking, security clearance and interpreter attendance where needed.',
}

const litigationCapacity: ServiceBlock = {
  id: 'litigation-capacity',
  title: 'Mental and Litigation Capacity',
  icon: Activity,
  cardSummary:
    'Decision-specific capacity assessments under the Mental Capacity Act 2005, including capacity to conduct proceedings.',
  intro:
    'Dr Syed considers instructions to assess whether a person has capacity to make a specific decision, applying the Mental Capacity Act 2005.',
  instructions: [
    'Capacity to conduct proceedings (litigation capacity)',
    'Capacity for specific decisions such as residence, care and contact',
    'Capacity to make medical treatment decisions',
    'Deprivation of Liberty Safeguards (DoLS) matters',
  ],
  questions: [
    'Is there an impairment of, or disturbance in, the functioning of the mind or brain?',
    'Can the person understand, retain, use or weigh the information relevant to the decision, and communicate it?',
    'Is any inability to decide caused by that impairment?',
    'Is the person’s capacity likely to change, and could support help them decide?',
  ],
}

const courtOfProtection: ServiceBlock = {
  id: 'court-of-protection',
  title: 'Court of Protection, Deputyship and Lasting Powers of Attorney',
  icon: Landmark,
  cardSummary:
    'Capacity evidence for property and affairs applications, deputyship and contested lasting powers of attorney.',
  intro:
    'Dr Syed considers instructions in Court of Protection property and affairs matters, including deputyship applications and disputes about lasting powers of attorney.',
  instructions: [
    'Capacity to manage property and financial affairs',
    'Assessments supporting deputyship applications, including COP3 evidence',
    'Contested lasting powers of attorney',
  ],
  questions: [
    'Does the person currently have capacity to manage their property and affairs?',
    'Did the donor have capacity when the lasting power of attorney was made?',
    'Is the person vulnerable to undue influence or exploitation?',
  ],
}

const testamentary: ServiceBlock = {
  id: 'testamentary',
  title: 'Testamentary and Retrospective Capacity',
  icon: ScrollText,
  cardSummary:
    'Opinion on capacity to make a will or gift, including retrospective assessment from records after death.',
  intro:
    'Dr Syed considers instructions on testamentary capacity and on capacity at a past date, where the opinion is formed from the available records and evidence.',
  instructions: [
    'Testamentary capacity, contemporaneous or retrospective',
    'Capacity to make lifetime gifts',
    'Retrospective capacity for other past decisions',
  ],
  questions: [
    'Did the person understand the nature and effect of the will, and the extent of their estate?',
    'Could they appreciate the claims of those who might expect to benefit?',
    'Was there a disorder of the mind that influenced the dispositions?',
    'How far do the available records allow a reliable retrospective opinion?',
  ],
}

const immigration: ServiceBlock = {
  id: 'immigration',
  title: 'Immigration, Asylum and Human Rights',
  icon: Globe,
  cardSummary:
    'Psychiatric reports for asylum and human rights claims, appeals and removal proceedings.',
  intro:
    'Dr Syed considers instructions from immigration solicitors and caseworkers for First-tier and Upper Tribunal appeals, fresh claims and judicial review.',
  instructions: [
    'Psychiatric reports for asylum and human rights claims and appeals',
    'Suicide and self-harm risk assessments in the context of removal',
    'Fitness to give evidence and vulnerable witness assessments',
  ],
  questions: [
    'Does the appellant have a mental disorder, and what is the diagnosis?',
    'Are the clinical findings consistent with the history given?',
    'What is the risk of suicide or self-harm, including on notification of removal and on return?',
    'Is the appellant fit to give evidence, and what adjustments would help?',
    'What treatment do they need, and what would be the likely effect of its interruption?',
  ],
}

const ptsd: ServiceBlock = {
  id: 'ptsd',
  title: 'PTSD and Complex Trauma',
  icon: HeartPulse,
  cardSummary:
    'Assessment of PTSD, complex PTSD and other trauma-related conditions, including Istanbul Protocol reports.',
  intro:
    'Dr Syed considers instructions to assess trauma-related psychiatric conditions in civil, immigration and criminal proceedings.',
  instructions: [
    'PTSD and complex PTSD assessments',
    'Reports following torture, trafficking or modern slavery',
    'Istanbul Protocol reports',
    'Psychiatric injury following accidents, assaults and other traumatic events',
  ],
  questions: [
    'Does the person meet diagnostic criteria for PTSD, complex PTSD or another condition?',
    'How severe is the condition, and how does it affect daily functioning?',
    'How consistent are the clinical findings with the events described?',
    'What treatment is recommended, and what is the likely prognosis?',
  ],
}

const clinicalNegligence: ServiceBlock = {
  id: 'clinical-negligence',
  title: 'Clinical Negligence and Personal Injury',
  icon: Stethoscope,
  cardSummary:
    'Psychiatric injury, causation, condition and prognosis in clinical negligence and personal injury claims.',
  intro:
    'Dr Syed considers instructions from claimant and defendant solicitors, and as a single joint expert, in clinical negligence and personal injury claims.',
  instructions: [
    'Condition and prognosis reports',
    'Causation of psychiatric injury',
    'Psychiatric injury following accidents, assaults or medical treatment',
  ],
  questions: [
    'Has the claimant suffered a recognised psychiatric injury, and what is the diagnosis?',
    'On the balance of probabilities, was the injury caused or made worse by the events in question?',
    'What was the claimant’s mental health before the index events?',
    'What treatment is needed, and what is the prognosis?',
    'How does the condition affect work, relationships and daily life?',
  ],
}

const employment: ServiceBlock = {
  id: 'employment',
  title: 'Employment and Professional Proceedings',
  icon: Briefcase,
  cardSummary:
    'Psychiatric evidence for Employment Tribunal claims and professional regulatory proceedings.',
  intro:
    'Dr Syed considers instructions in Employment Tribunal claims and in professional regulatory and fitness to practise proceedings.',
  instructions: [
    'Disability under the Equality Act 2010',
    'Psychiatric injury and injury to feelings arising from workplace events',
    'Health assessments in professional regulatory proceedings',
    'Fitness to participate in a hearing',
  ],
  questions: [
    'Does the claimant have a mental impairment with a substantial and long-term adverse effect on day-to-day activities?',
    'When did the condition begin, and what caused or contributed to it?',
    'What reasonable adjustments might be clinically relevant?',
    'Is the professional’s health relevant to their fitness to practise, and what is the prognosis?',
  ],
}

const family: ServiceBlock = {
  id: 'family',
  title: 'Family Proceedings',
  icon: Users,
  cardSummary:
    'Psychiatric assessment of adult parties where mental health is relevant to family proceedings.',
  intro:
    'Dr Syed considers instructions to assess adult parties in public and private law family proceedings. Expert evidence in family proceedings requires the court’s permission under Part 25 of the Family Procedure Rules.',
  instructions: [
    'Psychiatric assessment of parents and other adult parties',
    'Capacity to conduct family proceedings',
    'Treatment needs and prognosis relevant to care planning',
  ],
  questions: [
    'Does the parent have a mental disorder, and what is the diagnosis?',
    'How might the condition affect their ability to meet a child’s needs?',
    'What treatment is recommended, and over what timescale might change be expected?',
    'Does the parent have capacity to conduct the proceedings?',
  ],
}

const neurodevelopmental: ServiceBlock = {
  id: 'adhd',
  title: 'ADHD and Neurodevelopmental Assessments',
  icon: Brain,
  cardSummary:
    'Medico-legal assessment of ADHD and other neurodevelopmental conditions and their relevance to proceedings.',
  intro:
    'Dr Syed considers instructions to assess ADHD and other neurodevelopmental conditions where they may be relevant to criminal, civil, employment or family proceedings.',
  instructions: [
    'ADHD assessments for legal proceedings',
    'Assessment of other neurodevelopmental conditions, including autism',
    'Vulnerability and effective participation',
  ],
  questions: [
    'Does the person meet diagnostic criteria for ADHD or another neurodevelopmental condition?',
    'How does the condition affect attention, impulse control, communication and functioning?',
    'Is the condition relevant to the events in question, or to the person’s participation in proceedings?',
    'What adjustments or treatment are clinically indicated?',
  ],
}

// Order of the homepage cards.
export const homeServiceCards: Array<ServiceBlock & { link: string }> = [
  { ...criminal, link: '/services/forensic-risk#criminal' },
  { ...parole, link: '/services/forensic-risk#parole' },
  { ...litigationCapacity, link: '/services/mental-capacity#litigation-capacity' },
  { ...courtOfProtection, link: '/services/mental-capacity#court-of-protection' },
  { ...testamentary, link: '/services/mental-capacity#testamentary' },
  { ...immigration, link: '/services/specialist#immigration' },
  { ...ptsd, link: '/services/trauma-ptsd' },
  { ...clinicalNegligence, link: '/services/clinical-negligence' },
  { ...employment, link: '/services/specialist#employment' },
  { ...family, link: '/services/specialist#family' },
  { ...extradition, link: '/services/forensic-risk#extradition' },
  { ...neurodevelopmental, link: '/services/neurodevelopmental' },
  { ...prison, link: '/services/forensic-risk#prison' },
]

export const servicePages: Record<string, ServicePageContent> = {
  forensic: {
    path: '/services/forensic-risk',
    heroTitle: 'Criminal Psychiatric Reports and Fitness to Plead',
    heroSubtitle:
      'Psychiatric evidence for criminal courts, the Parole Board and extradition proceedings',
    intro:
      'Dr Imtiaz Syed provides independent psychiatric reports for criminal and related proceedings, for defence solicitors, prosecutors, the court and prisoners’ representatives. Each report answers the questions in the letter of instruction and sets out the reasoning behind every opinion.',
    assessmentNote:
      'Defendants and prisoners can be assessed by secure video where the establishment allows it, or face to face in prisons and secure settings.',
    blocks: [criminal, parole, extradition, prison],
    related: [
      { label: 'Mental and litigation capacity', path: '/services/mental-capacity' },
      { label: 'ADHD and neurodevelopmental assessments', path: '/services/neurodevelopmental' },
      { label: 'Immigration and asylum reports', path: '/services/specialist#immigration' },
    ],
  },
  capacity: {
    path: '/services/mental-capacity',
    heroTitle: 'Mental Capacity Assessments for Litigation and the Court of Protection',
    heroSubtitle:
      'Decision-specific capacity evidence under the Mental Capacity Act 2005',
    intro:
      'Dr Imtiaz Syed provides independent mental capacity assessments for litigation, the Court of Protection and probate disputes. Capacity is assessed for the specific decision in question, at the relevant time, applying the Mental Capacity Act 2005.',
    assessmentNote:
      'Capacity assessments can take place at the person’s home, in a care setting, face to face or by secure video, depending on what will give the most reliable assessment. Retrospective assessments are based on records and witness evidence.',
    blocks: [litigationCapacity, courtOfProtection, testamentary],
    related: [
      { label: 'Family proceedings', path: '/services/specialist#family' },
      { label: 'Clinical negligence and personal injury', path: '/services/clinical-negligence' },
      { label: 'Criminal reports and fitness to plead', path: '/services/forensic-risk' },
    ],
  },
  specialist: {
    path: '/services/specialist',
    heroTitle: 'Psychiatric Reports for Immigration, Employment and Family Proceedings',
    heroSubtitle:
      'Independent psychiatric evidence for tribunals and family courts',
    intro:
      'Dr Imtiaz Syed provides independent psychiatric reports for immigration and asylum appeals, Employment Tribunal and professional proceedings, and family proceedings. Each report addresses the legal framework the court or tribunal applies and answers the questions in the letter of instruction.',
    assessmentNote:
      'Assessments can take place by secure video, face to face, at home or in immigration detention and prison settings. Interpreters can be arranged where needed.',
    blocks: [immigration, employment, family],
    related: [
      { label: 'PTSD and complex trauma', path: '/services/trauma-ptsd' },
      { label: 'Mental and litigation capacity', path: '/services/mental-capacity' },
      { label: 'Extradition', path: '/services/forensic-risk#extradition' },
    ],
  },
  ptsd: {
    path: '/services/trauma-ptsd',
    heroTitle: 'PTSD and Complex Trauma Psychiatric Expert Reports',
    heroSubtitle:
      'Independent assessment of trauma-related conditions for legal proceedings',
    intro:
      'Dr Imtiaz Syed provides independent psychiatric reports on PTSD, complex PTSD and other trauma-related conditions. Reports distinguish clinical findings from opinion and consider alternative explanations for the presentation.',
    assessmentNote:
      'Trauma assessments are paced with care and can take place by secure video or face to face. Interpreters can be arranged where needed.',
    blocks: [ptsd],
    related: [
      { label: 'Immigration and asylum reports', path: '/services/specialist#immigration' },
      { label: 'Clinical negligence and personal injury', path: '/services/clinical-negligence' },
      { label: 'Employment proceedings', path: '/services/specialist#employment' },
    ],
  },
  clinicalNegligence: {
    path: '/services/clinical-negligence',
    heroTitle: 'Psychiatric Reports for Clinical Negligence and Personal Injury',
    heroSubtitle:
      'Independent evidence on psychiatric injury, causation and prognosis',
    intro:
      'Dr Imtiaz Syed provides independent psychiatric reports in clinical negligence and personal injury claims, for claimant and defendant solicitors and as a single joint expert. Reports comply with CPR Part 35 and set out the reasoning behind each opinion.',
    assessmentNote:
      'Claimants can be assessed by secure video, face to face or at home. A full review of medical records forms part of every report.',
    blocks: [clinicalNegligence],
    related: [
      { label: 'PTSD and complex trauma', path: '/services/trauma-ptsd' },
      { label: 'Mental and litigation capacity', path: '/services/mental-capacity' },
      { label: 'Employment proceedings', path: '/services/specialist#employment' },
    ],
  },
  neurodevelopmental: {
    path: '/services/neurodevelopmental',
    heroTitle: 'ADHD and Neurodevelopmental Medico-Legal Assessments',
    heroSubtitle:
      'Independent assessment of ADHD and related conditions for legal proceedings',
    intro:
      'Dr Imtiaz Syed provides independent medico-legal assessments of ADHD and other neurodevelopmental conditions. Reports consider developmental history, collateral information and alternative explanations before reaching a diagnostic opinion.',
    assessmentNote:
      'Assessments can take place by secure video, face to face or in prisons. Collateral information from family members, school records or previous assessments is valuable and requested where available.',
    blocks: [neurodevelopmental],
    related: [
      { label: 'Criminal reports and fitness to plead', path: '/services/forensic-risk' },
      { label: 'Employment proceedings', path: '/services/specialist#employment' },
      { label: 'Family proceedings', path: '/services/specialist#family' },
    ],
  },
}
